import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase.js';

// ─────────────────────────────────────────────────────────────────────────
// Schools — client library. There are NO direct Firestore reads on
// schools/** (rules deny them); every party gets exactly its slice via
// callables — see functions/schools.js for the access model.
// ─────────────────────────────────────────────────────────────────────────

const setSchoolCodeFn = httpsCallable(functions, 'setSchoolCode');
const requestSchoolJoinFn = httpsCallable(functions, 'requestSchoolJoin');
const decideSchoolJoinRequestFn = httpsCallable(functions, 'decideSchoolJoinRequest');
const startNewSchoolYearFn = httpsCallable(functions, 'startNewSchoolYear');
const addJoinReviewerFn = httpsCallable(functions, 'addJoinReviewer');
const removeJoinReviewerFn = httpsCallable(functions, 'removeJoinReviewer');
const getJoinRequestQueueFn = httpsCallable(functions, 'getJoinRequestQueue');
const getJoinRequestHistoryFn = httpsCallable(functions, 'getJoinRequestHistory');
const startSchoolTrialFn = httpsCallable(functions, 'startSchoolTrial');
const getSchoolDashboardFn = httpsCallable(functions, 'getSchoolDashboard');
const buySeatsFn = httpsCallable(functions, 'buySeats');
const setStudentPriceFn = httpsCallable(functions, 'setStudentPrice');
const setStudentGenerationFn = httpsCallable(functions, 'setStudentGeneration');
const setFreeContributorFn = httpsCallable(functions, 'setFreeContributor');
const addTeacherFn = httpsCallable(functions, 'addTeacher');
const removeTeacherFn = httpsCallable(functions, 'removeTeacher');
const addStudentEmailFn = httpsCallable(functions, 'addStudentEmail');
const addStudentEmailsBulkFn = httpsCallable(functions, 'addStudentEmailsBulk');
const removeStudentEmailFn = httpsCallable(functions, 'removeStudentEmail');
const bindTeacherToCourseFn = httpsCallable(functions, 'bindTeacherToCourse');
const unbindTeacherFromCourseFn = httpsCallable(functions, 'unbindTeacherFromCourse');
const listCourseBindingsFn = httpsCallable(functions, 'listCourseBindings');
const getMyTeachingCoursesFn = httpsCallable(functions, 'getMyTeachingCourses');
const getBoundCourseQuizResultsFn = httpsCallable(functions, 'getBoundCourseQuizResults');

// Student: link a school code to their profile (null/'' unlinks). Returns
// { linked, schoolName?, role? }. There's no discount — linking simply gives
// the student their school-covered access (viewing, and generation if the
// school has enabled it).
export async function setSchoolCode(code) {
  const { data } = await setSchoolCodeFn({ code: code || null, idempotencyKey: crypto.randomUUID() });
  return data;
}

// Student: submit a self-service join request for a school code — does NOT
// grant access by itself. Returns { status: 'pending'|'approved', schoolName }.
// The school owner must call decideSchoolJoinRequest to actually grant it.
export async function requestSchoolJoin(code) {
  const { data } = await requestSchoolJoinFn({ code, idempotencyKey: crypto.randomUUID() });
  return data;
}

// School owner OR a delegated join reviewer (see addJoinReviewer): approve
// or disallow one pending join request.
export async function decideSchoolJoinRequest(uid, decision) {
  const { data } = await decideSchoolJoinRequestFn({ uid, decision, idempotencyKey: crypto.randomUUID() });
  return data;
}

// School owner: start a new academic year — every student whose access came
// from an approved join request (not the roster, not a legacy instant link)
// silently lapses on their next sign-in and must re-request + be re-approved.
export async function startNewSchoolYear() {
  const { data } = await startNewSchoolYearFn({ idempotencyKey: crypto.randomUUID() });
  return data;
}

// School owner: designate / remove an account that can also approve/
// disallow join requests, without giving them anything else on the
// dashboard (no wallet, seats, teachers, or pricing).
export async function addJoinReviewer(email) {
  const { data } = await addJoinReviewerFn({ email });
  return data;
}
export async function removeJoinReviewer(reviewerUid) {
  const { data } = await removeJoinReviewerFn({ reviewerUid });
  return data;
}

// Owner OR a delegated reviewer: the narrow join-request queue view — used
// by the reviewer-only page (someone who isn't the school owner).
export async function getJoinRequestQueue() {
  const { data } = await getJoinRequestQueueFn({});
  return data;
}

// Owner OR a delegated reviewer: the full requested/approved/disallowed
// timeline for one student's join history at this school.
export async function getJoinRequestHistory(uid) {
  const { data } = await getJoinRequestHistoryFn({ uid });
  return data.events || [];
}

// Self-serve: turn the signed-in account into a school partner with a free,
// viewing-only trial (no admin step). Requires a verified mobile number to
// already be linked to the account (see views/schoolStart.js). Returns
// { ok, code, name, trialEndsAtMs, seatsLicensed }.
export async function startSchoolTrial(name) {
  const { data } = await startSchoolTrialFn({ name, idempotencyKey: crypto.randomUUID() });
  return data;
}

// School owner: their own school's data — seats, term, wallet, student-
// generation toggle, teachers, ledger. Throws permission-denied otherwise.
export async function getSchoolDashboard() {
  const { data } = await getSchoolDashboardFn({});
  return data;
}

// School owner: buy/renew `qty` student seats for one term (charged from the
// school wallet). Returns { ok, seatsLicensed, seatTermEndsMs, costPaise, balancePaise }.
export async function buySeats(qty) {
  const { data } = await buySeatsFn({ qty, idempotencyKey: crypto.randomUUID() });
  return data;
}

// School owner: set what the school charges each student (inclusive of
// taxes) — turns on automatic invoicing for rostered students at sign-in.
// The amount above the platform's fixed charge appears on the invoice as
// "Instructor Charges". Pass null/0 to turn invoicing off.
export async function setStudentPrice(studentPriceRupees) {
  const { data } = await setStudentPriceFn({ studentPriceRupees });
  return data;
}

// School owner: turn student content-generation on/off (billed to the school
// wallet when on). Returns { ok, studentsCanGenerate }.
export async function setStudentGeneration(enabled) {
  const { data } = await setStudentGenerationFn({ enabled: !!enabled });
  return data;
}

// School owner: opt into (or out of) the free-content reciprocity network —
// see functions/schools.js setFreeContributor doc comment. The caller (see
// views/school.js) is responsible for showing the trade-off explanation
// BEFORE calling this with enabled: true; this function itself just flips
// the flag. Returns { ok, isFreeContributor }.
export async function setFreeContributor(enabled) {
  const { data } = await setFreeContributorFn({ enabled: !!enabled });
  return data;
}

// School owner: designate / remove a teacher (teacher-free tier).
export async function addTeacher(email) {
  const { data } = await addTeacherFn({ email });
  return data;
}
export async function removeTeacher(teacherUid) {
  const { data } = await removeTeacherFn({ teacherUid });
  return data;
}

// School owner: add / remove a student by email (roster). The email need not
// have an account yet — it's auto-recognised whenever that person signs in.
export async function addStudentEmail(email) {
  const { data } = await addStudentEmailFn({ email });
  return data;
}
// Bulk add from a parsed spreadsheet. Server is all-or-nothing on capacity:
// if the new students exceed free seats it throws and writes nothing. Returns
// { added, skippedExisting, skippedTeachers, total }.
export async function addStudentEmailsBulk(emails) {
  const { data } = await addStudentEmailsBulkFn({ emails });
  return data;
}
export async function removeStudentEmail(email) {
  const { data } = await removeStudentEmailFn({ email });
  return data;
}

// School owner: bind/unbind one of the school's teachers to one of the
// owner's own courses (self-authored or a remix-copied subscription — see
// functions/schools.js for why view-only subscriptions can't be bound).
export async function bindTeacherToCourse(teacherUid, courseId) {
  const { data } = await bindTeacherToCourseFn({ teacherUid, courseId });
  return data;
}
export async function unbindTeacherFromCourse(teacherUid, courseId) {
  const { data } = await unbindTeacherFromCourseFn({ teacherUid, courseId });
  return data;
}
// School owner: { bindings: [{ courseId, courseTitle, teacherUids }] } —
// every course's current binding list, for the Teachers panel's course picker.
export async function listCourseBindings() {
  const { data } = await listCourseBindingsFn({});
  return data.bindings || [];
}

// Teacher: { courses: [{ courseId, courseTitle }] } — the courses this
// signed-in teacher is bound to at their school.
export async function getMyTeachingCourses() {
  const { data } = await getMyTeachingCoursesFn({});
  return data.courses || [];
}
// Teacher: quiz results across every topic in one bound course they're
// verified to be bound to (server re-checks — see functions/schools.js).
export async function getBoundCourseQuizResults(courseId) {
  const { data } = await getBoundCourseQuizResultsFn({ courseId });
  return data.results || [];
}
